import { client } from '../../../../../App';
import { GET_AUTOMATED_COLUMNS } from '../../../../../queries/widgets';

/** Same fields the PDF dialog fetches (PdfPreviewDialog/index.tsx). */
export const MASTER_DATA_FIELDS = [
  'fondskategorie', 'auflagedatum', 'wkn', 'isin', 'erstausgabepreis', 'fondsvolumen',
  'kursNav', 'fondswaehrung', 'fondsdomizil', 'geschaeftsjahresende', 'ertragsverwendung',
  'rechtsform', 'risikoErtragsprofil', 'sparplanfaehig', 'mindestzeichnungbetrag',
];
export const FEE_FIELDS = ['ausgabeaufschlag', 'laufendeKostenTer', 'davonVerwaltungsgebuehr'];

/** published_at → YYYY-MM-DD, as the preview and the PDF dialog derive `until`. */
export function publishedAtToUntil(dateStr?: string): string | undefined {
  if (!dateStr) return undefined;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return undefined;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Every dynamic value FactsheetPDF reads from `cellDataCache`, under the keys it reads
 * (`cumulative-row-col-until`, `section.field-until`). The publish path renders the PDF
 * without the dialog, so without this it printed empty cells and the hard-coded defaults
 * (e.g. Kurs 146,39 EUR). A failed fetch throws: better no upload than wrong numbers.
 */
export async function fetchDynamicValueCache(
  data: any,
  isin: string,
): Promise<{ cellDataCache: Map<string, any>; until?: string }> {
  const until = publishedAtToUntil(data?.published_at);
  const cellDataCache = new Map<string, any>();
  if (!isin) return { cellDataCache, until };

  const isDynamic = (value: any) => value?.isDynamic && value?.fields?.length;
  const jobs: Array<[string, any]> = [];
  (data?.cumulativePerformance?.values || []).forEach((row: any[], rowIndex: number) => {
    (row || []).forEach((cell: any, colIndex: number) => {
      if (isDynamic(cell)) jobs.push([`cumulative-${rowIndex}-${colIndex}-${until || ''}`, cell]);
    });
  });
  ([['masterData', MASTER_DATA_FIELDS], ['fees', FEE_FIELDS]] as const).forEach(([section, fields]) => {
    fields.forEach((field) => {
      const value = data?.[section]?.[field];
      if (isDynamic(value)) jobs.push([`${section}.${field}-${until || ''}`, value]);
    });
  });

  await Promise.all(
    jobs.map(async ([key, value]) => {
      const res = await client.query({
        query: GET_AUTOMATED_COLUMNS,
        variables: { param: { data: [value], isin, until } },
      });
      const fetched = res.data.getAutomatedColumns?.[0]?.[0];
      if (fetched !== undefined && fetched !== null) cellDataCache.set(key, fetched);
    }),
  );
  return { cellDataCache, until };
}
